import { useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router';
import { Button } from '@/components/Button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/shadcn-ui/card';
import { Input } from '@/components/shadcn-ui/input';
import { Label } from '@/components/shadcn-ui/label';
import { useAuth } from '@/contexts/auth';
import { logError } from '@/services/logger';

interface LocationState {
  from?: {
    pathname: string;
  };
}

export function SignInScene() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { signIn, user } = useAuth();
  const location = useLocation();

  // Get the redirect path from location state or default to home
  const state = location.state as LocationState | null;
  const from = state?.from?.pathname || '/';

  // If user is already logged in, redirect to the intended destination
  if (user) {
    return <Navigate to={from} replace />;
  }

  const handleSubmit = (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    // Using void to indicate we're not using the promise result directly in JSX
    void (async () => {
      try {
        const { error: signInError } = await signIn(email, password);

        if (signInError) {
          setError(signInError.message || 'Failed to sign in');
          setIsLoading(false);
          return;
        }

        // Navigation will happen automatically due to the if (user) check
      } catch (error) {
        setError('An unexpected error occurred');
        logError(error, 'Failed to sign in');
        setIsLoading(false);
      }
    })();
  };

  return (
    <div className="container flex h-screen w-screen flex-col items-center justify-center">
      <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px]">
        <Card>
          <CardHeader>
            <CardTitle>Welcome back</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit}>
              <div className="flex flex-col gap-6">
                {error && (
                  <div className="relative rounded border border-red-400 bg-red-100 px-4 py-3 text-red-700">
                    {error}
                  </div>
                )}
                <div className="grid gap-3">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@gmail.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                    }}
                    required
                    autoFocus
                  />
                </div>
                <div className="grid gap-3">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                    }}
                    required
                  />
                </div>
                <Button
                  type="submit"
                  className="order-1 w-full"
                  disabled={isLoading}
                >
                  {isLoading ? 'Signing in...' : 'Sign In'}
                </Button>
                <Link
                  to="/forgot-password"
                  className="-mt-4 self-end text-sm font-medium text-muted-foreground underline-offset-4 hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="mt-4 text-center text-sm text-muted-foreground">
                Don&#x2019;t have an account?{' '}
                <Link
                  to="/sign-up"
                  className="font-medium underline-offset-4 hover:underline"
                >
                  Sign up
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
